import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { useCallback } from 'react';
import { QUERY_KEYS } from '@restworld/utils-common';
import { useJobPositionsService } from '@restworld/data-services';
import { useTranslation } from 'next-i18next';
import { showToast } from '@restworld/ui-ds-base';

export const deleteJobPositionDraftModalAtom = atom<boolean>(false);
export const jobPositionIdToDeleteAtom = atom<string | null>('');

export function useDeleteJobPositionDraft() {
  const queryClient = useQueryClient();
  const jobPositionsService = useJobPositionsService();
  const { t } = useTranslation('job-positions-page');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useAtom(deleteJobPositionDraftModalAtom);
  const [jpIdToDelete, setJpIdToDelete] = useAtom(jobPositionIdToDeleteAtom);

  const { mutate: deleteJobPositionDraft, isPending: isDeleting } = useMutation({
    mutationFn: ({ jobPositionId }: { jobPositionId: string }) => jobPositionsService.deleteJobPosition(jobPositionId),
    onSuccess: () => {
      // Invalidate the job positions list query to refresh the data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.JOB_POSITIONS.LIST],
      });
      // Close the modal on success
      setIsDeleteModalOpen(false);
      setJpIdToDelete(null);
    },
    onError: () => {
      // Show error toast with high z-index to appear above modal
      showToast(t('delete_draft_error_message'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
    },
  });

  const handleDeleteDraft = useCallback(
    (jpId: string) => {
      setJpIdToDelete(jpId);
      setIsDeleteModalOpen(true);
    },
    [setJpIdToDelete, setIsDeleteModalOpen]
  );

  const handleConfirmDelete = useCallback(() => {
    if (jpIdToDelete) {
      deleteJobPositionDraft({ jobPositionId: jpIdToDelete });
    }
  }, [jpIdToDelete, deleteJobPositionDraft]);

  const handleCloseDeleteModal = useCallback(() => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setJpIdToDelete(null);
    }
  }, [isDeleting, setIsDeleteModalOpen, setJpIdToDelete]);

  return {
    deleteJobPositionDraft,
    isDeleting,
    isDeleteModalOpen,
    jpIdToDelete,
    handleDeleteDraft,
    handleConfirmDelete,
    handleCloseDeleteModal,
  };
}
